import styled from "@emotion/styled";
import { Stack } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useBanks,
  useChartOfAccounts,
  useConfirmReconcileCheque,
  useRevenueStore
} from "features/finances";
import moment from "moment";
import { useState } from "react";
import { EthiopianDateField } from "../EthiopianDateField";
import { BaseModal } from "./BaseModal";

const SmallText = styled.span`
  color: #8e8e8e;
  font-size: 10px;
  font-weight: 500;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
  background-color: transparent;
`;

export function ChequeReconcileModal({ data }) {
  const chequeReconcileOpen = useRevenueStore(
    state => state.chequeReconcileOpen
  );
  const closeChequeReconcileModal = useRevenueStore(
    state => state.closeChequeReconcileModal
  );
  const [method, setMethod] = useState("cheque");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [depositorName, setDepositorName] = useState("");
  const [depositBank, setDepositBank] = useState("");
  const [depositDate, setDepositDate] = useState("");
  const confirmReconcileMutation = useConfirmReconcileCheque();
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const chartOfAccountsQuery = useChartOfAccounts({
    churchId: selectedChurch?.id,
    filters: [
      { column: "account_type", operator: "in", value: ["INCOME", "EQUITY"] }
    ]
  });

  const banksQuery = useBanks({
    churchId: selectedChurch?.id
  });

  const approveCheque = () => {
    confirmReconcileMutation.mutate({
      churchId: selectedChurch?.id,
      chequeId: data?.id,
      chequeData: {
        receipt_number: receiptNumber,
        depositor_name: depositorName,
        deposit_date: depositDate,
        bank_id: depositBank
      }
    });
  };

  return (
    <BaseModal
      title="Cheque Reconcile"
      isOpen={chequeReconcileOpen}
      onClose={closeChequeReconcileModal}
      footer={
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <div />
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              flavor="white"
              size="small"
              onClick={closeChequeReconcileModal}
              disabled={confirmReconcileMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={approveCheque}
              disabled={
                !receiptNumber ||
                !depositorName ||
                !depositDate ||
                !depositBank ||
                confirmReconcileMutation.isLoading
              }
            >
              Approve Cheque
            </Button>
          </Stack>
        </Stack>
      }
    >
      <SmallText>Transaction Number {data?.transaction_number}</SmallText>
      <Field>
        <Label htmlFor="fullName">Full Name</Label>
        <Input
          id="fullName"
          defaultValue={
            data?.full_name ||
            data?.members
              .map(member => `${member.first_name} ${member.last_name}`)
              .join(", ")
          }
          disabled
        />
      </Field>
      <Field>
        <Label htmlFor="revenueType">Revenue type or reason</Label>
        <Select
          id="chart_of_account_id"
          value={data?.chart_of_account_id}
          disabled
        >
          {chartOfAccountsQuery.data?.result?.map(chartOfAccount => (
            <option key={chartOfAccount.id} value={chartOfAccount.id}>
              {chartOfAccount.account_name}
            </option>
          ))}
        </Select>
      </Field>
      <Field>
        <Label htmlFor="description">Description</Label>
        <Input id="description" value={data?.description} disabled />
      </Field>
      <Field>
        <Label htmlFor="method">Method</Label>
        <Select
          id="method"
          value={method}
          onChange={e => setMethod(e.target.value)}
          disabled
        >
          <option value="cash">Cash</option>
          <option value="cheque">Cheque</option>
          <option value="bankTransfer">Bank Transfer</option>
          <option value="mobileTransfer">Mobile Transfer</option>
        </Select>
      </Field>
      <Field>
        <Label htmlFor="checkNumber">Check Number</Label>
        <Input id="checkNumber" value={data?.cheque_number} disabled />
      </Field>
      <Field>
        <Label htmlFor="bank">Bank</Label>
        <Input id="bank" value={data?.bank_name} disabled />
      </Field>
      <Field>
        <Label htmlFor="accountHolder">Account Holder Full Name</Label>
        <Input id="accountHolder" value={data?.account_holder_name} disabled />
      </Field>
      <Field>
        <Label htmlFor="accountNumber">Account Number</Label>
        <Input id="accountNumber" value={data?.account_number} disabled />
      </Field>
      <Field>
        <Label htmlFor="phoneNumber">Phone Number</Label>
        <Input id="phoneNumber" value={data?.phone_number} disabled />
      </Field>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="amount">Amount</Label>
          <Input id="amount" value={data?.amount} disabled />
        </Field>
        <Field>
          <Label htmlFor="currency">Currency</Label>
          <Select id="currency" value={data?.currency} disabled>
            <option>ETB</option>
          </Select>
        </Field>
      </Stack>
      <Field>
        <Label htmlFor="receiptNumber">Deposit receipt number</Label>
        <Input
          id="receiptNumber"
          value={receiptNumber}
          onChange={e => setReceiptNumber(e.target.value)}
        />
      </Field>
      <Field>
        <Label htmlFor="depositorName">Depositor name</Label>
        <Input
          id="depositorName"
          value={depositorName}
          onChange={e => setDepositorName(e.target.value)}
        />
      </Field>
      <Field>
        <Label htmlFor="depositBank">Deposit Bank</Label>
        <Select
          id="depositBank"
          value={depositBank}
          onChange={e => setDepositBank(e.target.value)}
        >
          <option value="" disabled>
            Select Bank
          </option>
          {banksQuery.data?.result?.map(bank => (
            <option key={bank.id} value={bank.id}>
              {bank.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field>
        <Label htmlFor="depositDate">Deposit date</Label>
        <EthiopianDateField
          size="small"
          value={depositDate}
          onChange={e => setDepositDate(e.target.value)}
        />
      </Field>
      <Field>
        <Label htmlFor="receivedDate">Received date</Label>
        <Input
          id="receivedDate"
          type="date"
          value={new moment(data?.received_date).format("YYYY-MM-DD")}
          disabled
        />
      </Field>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="dueDate">Due date</Label>
          <Input
            id="dueDate"
            type="date"
            value={new moment(data?.cheque_due_date).format("YYYY-MM-DD")}
            disabled
          />
        </Field>
        <Field>
          <Label htmlFor="chequeReceivedDate">Cheque Received date</Label>
          <Input
            id="chequeReceivedDate"
            type="date"
            value={new moment(data?.received_date).format("YYYY-MM-DD")}
            disabled
          />
        </Field>
      </Stack>

      <MutationError error={confirmReconcileMutation.error} sx={{ mt: 2 }} />
    </BaseModal>
  );
}
